import { FocusEvent, forwardRef, useCallback, useEffect, useRef, useState } from 'react';

interface Props {
    onChange?: (value: string) => void;
    onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
    onClick?: () => void;
    onEnterKey?: (ref: HTMLInputElement) => void;
    onEscKey?: () => void;
    truncated?: boolean;
    ref?: HTMLInputElement;
    value?: string;
    minWidthInPx?: number;
    maxWidthInPx?: number;
    className?: string;
    maxLength?: number;
    disableInput?: boolean;
}

export const GrowingInput = forwardRef<HTMLInputElement, Props>((props: Props, ref) => {
    const inputRef = useRef(!ref || !ref['current'] ? null : ref['current']);
    const {
        onChange,
        onFocus,
        onClick,
        onEnterKey,
        onEscKey,
        value = '',
        minWidthInPx,
        maxWidthInPx,
        className,
        maxLength,
        disableInput = false
    } = props;
    const [inputValue, setInputValue] = useState('');
    const [inputWidth, setInputWidth] = useState<string | number>(0);

    const calculateWordWidth = useCallback(
        (text: string) => {
            const div = document.createElement('div');
            div.setAttribute('class', 'textDimensionCalculation');
            div.style['white-space'] = 'pre';
            div.style['position'] = 'absolute';
            div.style['visibility'] = 'hidden';
            if (ref && ref['current']) {
                div.style['font-size'] = window.getComputedStyle(ref['current'], null).getPropertyValue('font-size');
                div.style['font-weight'] = window
                    .getComputedStyle(ref['current'], null)
                    .getPropertyValue('font-weight');
            }

            div.innerHTML = text;

            document.body.appendChild(div);
            const width = Math.ceil(div.getBoundingClientRect().width);
            div.parentNode?.removeChild(div);
            return width;
        },
        [ref]
    );

    const onValueChange = useCallback(
        (value: string, propagateChange: boolean) => {
            const width = calculateWordWidth(value);
            setInputValue(value);
            setInputWidth(width);
            if (onChange && propagateChange) {
                onChange(value);
            }
        },
        [calculateWordWidth, onChange]
    );

    function onListNameKeyUp(event: { key: string }) {
        if (onEnterKey && event.key === 'Enter') {
            onEnterKey(inputRef.current);
        }
        if (onEscKey && event.key === 'Escape') {
            onEscKey();
        }
    }

    let width = inputWidth;
    if (minWidthInPx && minWidthInPx > width) {
        width = minWidthInPx;
    }

    useEffect(() => {
        if (value !== inputValue) {
            onValueChange(value, false);
        }
    }, [inputValue, onValueChange, value]);

    return (
        <input
            ref={ref}
            data-testid="growing-inputfield"
            onClick={onClick}
            onChange={event => onValueChange(event.target.value, true)}
            value={inputValue}
            style={{
                width: width > 0 ? `${width}px` : undefined,
                maxWidth: maxWidthInPx ? `${maxWidthInPx}px` : undefined
            }}
            onKeyUp={onListNameKeyUp}
            className={className}
            maxLength={maxLength}
            onFocus={onFocus}
            disabled={disableInput}
        />
    );
});
